import { formatNegativeCategory, formatNegativeSubCategory } from './negativeLabels';

/**
 * Поля звонка, которые заполняет один из двух движков анализа.
 *
 * В режиме пайплайна нод legacy-колонки таблицы `calls` (primary_intent, booking_*,
 * quality_form, checklist_json, is_complaint) не заполняются вовсе — те же смысловые
 * значения приезжают из `call_node_facts` полями `node*`.
 *
 * Порядок везде legacy → нода → прочерк. У одного звонка заполнен ровно один движок,
 * поэтому на корректность порядок не влияет, зато гарантирует прежнюю выдачу тенантам
 * на старом движке.
 */

/** Коды итога звонка из контракта triage — фиксированы для всех тенантов. */
const NODE_CALL_OUTCOME_CODES = {
  ACHIEVED: 'achieved',
  PENDING: 'pending_specific_action',
  NOT_ACHIEVED: 'not_achieved',
  CANCELLED: 'cancelled_or_refused',
  UNKNOWN: 'unknown'
};

/** Внутренний/партнёрский звонок: в legacy это классификация, в режиме нод — тип обращения triage. */
const INTERNAL_CALL_CODE = 'internal_or_partner';

const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

/**
 * Подпись типа обращения. `topic` — последняя страховка: в режиме нод он уже содержит
 * русскую подпись типа звонка из triage, поэтому заполнен даже у звонков без фактов.
 */
export const resolveCallTypeLabel = (call) =>
  call?.primaryIntentLabel
  ?? call?.primaryIntent
  ?? call?.nodeCallTypeLabel
  ?? call?.nodeCallTypeCode
  ?? call?.topic
  ?? null;

/**
 * Итог звонка вместе с источником: карты цветов у движков разные, коды не пересекаются.
 * Возвращает `null`, когда итога нет ни у одного движка.
 *
 * В режиме нод значение приходит из выхода, выбранного в настройках полей карточки (по
 * умолчанию — `call_outcome` triage). Коды чужого выхода с картой цветов triage совпадать не
 * обязаны — тогда бейдж нейтральный.
 */
export const resolveCallOutcome = (call) => {
  // bookingApplicable === false — это осознанный прочерк legacy-воронки, а не отсутствие данных.
  if (call?.bookingApplicable !== false) {
    const legacy = call?.bookingOutcomeLabel ?? call?.bookingOutcome;
    if (legacy) {
      return {
        label: legacy,
        code: call?.bookingOutcome ?? null,
        source: 'legacy',
        reason: call?.bookingOutcomeEvidence ?? null
      };
    }
  }

  const node = call?.nodeCallOutcomeLabel ?? call?.nodeCallOutcomeCode;
  if (node) {
    return {
      label: node,
      code: call?.nodeCallOutcomeCode ?? null,
      source: 'node',
      reason: call?.nodeCallOutcomeReason ?? null
    };
  }

  return null;
};

/**
 * Поле «Услуга». У legacy-движка это таксономия эндо-инсталляций, в режиме нод — выход,
 * выбранный в настройках полей карточки (сервер кладёт его в `nodeService*`). Без настройки
 * у звонка нод значения нет — прочерк, как и раньше.
 */
export const resolveCallService = (call) =>
  call?.serviceAreaLabel
  ?? call?.serviceArea
  ?? call?.nodeServiceLabel
  ?? call?.nodeServiceCode
  ?? null;

/**
 * Типы жалобы по данным нод: подписи категорий, иначе их коды, иначе сам факт жалобы.
 * Нода детализации могла быть пропущена входным фильтром — факт жалобы всё равно есть.
 */
const nodeComplaintTypes = (call) => {
  const nodeLabels = asArray(call?.nodeComplaintCategoryLabels);
  const nodeCodes = asArray(call?.nodeComplaintCategories);
  const nodeCategories = nodeLabels.length ? nodeLabels : nodeCodes;
  if (nodeCategories.length) {
    return nodeCategories;
  }

  return call?.nodeComplaintFlag ? ['Жалоба'] : [];
};

/**
 * Перечень жалоб строкой. Подписи категорий нод приходят с сервера из справочника пакета
 * и НЕ прогоняются через formatNegativeCategory: его humanizeCode превратит
 * `product_or_service_quality_issue` в «Product Or Service Quality Issue».
 */
export const resolveComplaints = (call) => {
  const categories = asArray(call?.negativeCategories);
  const subCategories = asArray(call?.negativeSubCategories);
  if (categories.length || subCategories.length) {
    return [
      ...categories.map(formatNegativeCategory),
      ...subCategories.map(formatNegativeSubCategory)
    ].join(', ');
  }

  const nodeTypes = nodeComplaintTypes(call);
  return nodeTypes.length ? nodeTypes.join(', ') : '—';
};

/**
 * Типы жалобы звонка списком — для бейджей. В отличие от `resolveComplaints`, без
 * подкатегорий: у них своя колонка «Детализация». Пустой список — жалобы нет.
 *
 * В режиме нод legacy-массив `negativeCategories` пуст всегда, и таблица, читавшая только
 * его, показывала прочерк у каждого звонка кейса, хотя тип самого кейса собран сервером
 * из тех же полей нод.
 */
export const resolveComplaintTypes = (call) => {
  const categories = asArray(call?.negativeCategories);
  if (categories.length) {
    return categories.map(formatNegativeCategory);
  }

  return nodeComplaintTypes(call);
};

/** Оценка чек-листа. Слияние движков сделано на сервере, шкала у обоих 0..10. */
export const resolveChecklistScore = (call) =>
  call?.checklistScore === undefined ? null : call.checklistScore;

export const resolveObjectionLabels = (call) => {
  const legacyLabels = asArray(call?.objectionLabels);
  if (legacyLabels.length) {
    return legacyLabels;
  }

  const legacyCodes = asArray(call?.objections);
  if (legacyCodes.length) {
    return legacyCodes;
  }

  const nodeLabels = asArray(call?.nodeObjectionLabels);
  return nodeLabels.length ? nodeLabels : asArray(call?.nodeObjections);
};

/** Форма чек-листа: в режиме нод её роль играет название отработавшей чек-лист-ноды. */
export const resolveChecklistFormLabel = (call) =>
  call?.qualityFormLabel ?? call?.qualityForm ?? call?.nodeChecklistFormLabel ?? null;

/** Итог чек-листа в процентах: оценка 0..10 переводится в 0..100. */
export const resolveOperatorTotal = (call) => {
  if (call?.firstOperatorTotal !== null && call?.firstOperatorTotal !== undefined) {
    return call.firstOperatorTotal;
  }

  const score = resolveChecklistScore(call);
  return score === null || score === undefined ? null : Number(score) * 10;
};

export const isInternalCall = (call) =>
  call?.callClassificationType === INTERNAL_CALL_CODE
  || call?.nodeCallTypeCode === INTERNAL_CALL_CODE;

/**
 * Краткое содержание разговора. Колонка `calls.summary` общая для обоих движков: в режиме нод
 * её заполняет triage. Пустая строка и пробелы — это «нет данных», блок карточки не рисуется.
 */
export const resolveCallSummary = (call) => {
  const summary = typeof call?.summary === 'string' ? call.summary.trim() : '';
  return summary || null;
};

export { NODE_CALL_OUTCOME_CODES };
